/**
 * ERP 문서 줄 편집기 (E1 · E7). erp.js 가 import 한다.
 * setupERPLines(rows, {items, lines, L, money, num, taxDefault, taxMode(), rounding, docDiscount(),
 *                      locked, canManage, post, onChange, onTotals}) → api
 *   api.collect()   현재 줄 배열 [{item_id,name,spec,unit,note,qty,unit_price,discount_rate,tax_type,tax_rate}] (품목명 없는 줄 제외)
 *   api.render(lines) / api.addLine() / api.recalc() → {sub, discount, tax, total} / api.serialize()
 *   api.setLocked(bool) / api.setTaxMode(mode) / api.showDiscountCol(bool) / api.hasDiscount()
 * 열: ⋮⋮ | 품목명(+코드 배지, 비고 서브 줄) | 규격 | 수량 | 단위 | 단가 | 할인% | 세율(필) | 공급가액 | 세액 | ✕
 * 계산은 서버 models.LineTotals 를 그대로 미러한다:
 *   amount = round(qty×단가×(100−할인%)/100); exclusive → supply = amount; inclusive → 과세 줄만 tax = floor|round(amount×rate/(100+rate)), supply = amount − tax (입력 금액 보존);
 *   none → 세액 0 (tax_type 보존); 문서 할인은 과세 줄 공급가액 비율로 안분(floor, 잔여는 마지막 과세 줄);
 *   줄 세액 = floor|round(supply_after×rate/100), 합계 = supply − discount + tax.
 * 품목 피커: 품목명 입력 중 또는 `/` → .prop-pop 리스트(부분 문자열 + 초성), ↑↓ Enter 선택, Esc 자유 텍스트,
 *   '새 품목: …' (manage) → POST /api/erp/items. 세율 셀은 openPillPicker(과세 10% / 영세 0% / 면세).
 * 키보드: Tab 좌→우, 마지막 줄 세액에서 Tab → 새 줄, Enter → 다음 줄(마지막이면 추가), Shift+Enter → 아래 삽입,
 *   Backspace(빈 품목명) → 줄 제거 + 실행 취소, Alt+↑/↓ 순서, Ctrl+D 복제, ⋮⋮ 드래그로 순서 변경.
 */
import { showContextMenu } from './context-menu.js';
import { showToast } from './calendar-history.js';
import { openPillPicker, closePillPicker } from './prop-picker.js';

const TAX_TYPES = ['taxable', 'zero', 'exempt'];
const TAX_RATE = { taxable: 10, zero: 0, exempt: 0 };
const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];

function chosung(str) {
    return Array.from(str || '').map((ch) => {
        const c = ch.charCodeAt(0);
        if (c >= 0xAC00 && c <= 0xD7A3) return CHO[Math.floor((c - 0xAC00) / 588)];
        return ch;
    }).join('');
}
const isChosungQuery = (q) => q.length > 0 && Array.from(q).every((ch) => CHO.includes(ch));
function itemMatches(it, q) {
    if (!q) return true;
    const lq = q.toLowerCase();
    if ((it.name || '').toLowerCase().includes(lq) || (it.code || '').toLowerCase().includes(lq)) return true;
    return isChosungQuery(q) && chosung(it.name).includes(q);
}

export function setupERPLines(rows, opts) {
    const o = opts || {};
    const L = o.L || {};
    let items = (o.items || []).slice();
    let acctPrices = o.accountPrices || {}; // C-4 §8.3 거래처별 단가 (item id → 단가)
    const money = o.money || ((n) => (Number(n) || 0).toLocaleString('ko-KR'));
    const num = o.num || ((v) => Number(String(v).replace(/[^0-9.-]/g, '')) || 0);
    const taxDefault = Number.isFinite(Number(o.taxDefault)) ? Number(o.taxDefault) : 10;
    const defaultType = taxDefault === 0 ? 'zero' : 'taxable';
    const taxMode = () => (o.taxMode ? o.taxMode() : 'exclusive');
    const rounding = () => (o.rounding || 'floor');
    const docDiscount = () => Math.max(0, Math.round(o.docDiscount ? num(o.docDiscount()) : 0));
    let locked = !!o.locked;
    let showDisc = o.showDiscount !== false; // §9: 할인(%) 열 기본 표시
    const notify = () => { if (o.onChange) o.onChange(); };
    const typeLabel = (t) => (t === 'zero' ? (L.taxZero || '영세 0%') : t === 'exempt' ? (L.taxExempt || '면세') : (L.taxTaxable || '과세 10%'));
    const blank = () => ({ name: '', spec: '', unit: '', note: '', qty: 1, unit_price: 0, discount_rate: 0, tax_type: defaultType, tax_rate: taxDefault });
    const container = rows.closest('.erp-lines') || rows.parentElement;

    function setTaxPill(r, type) {
        const t = TAX_TYPES.includes(type) ? type : defaultType;
        r.dataset.taxType = t;
        const b = r.querySelector('.erp-tax-pill');
        b.className = 'prop-pill neutral erp-tax-pill t-' + t;
        b.textContent = typeLabel(t);
    }
    function setCode(r, code) {
        const badge = r.querySelector('.erp-item-code');
        badge.textContent = code || '';
        badge.hidden = !code;
    }
    function setNote(r, note, show) {
        const n = r.querySelector('.erp-note');
        n.value = note || '';
        n.hidden = !(show || note);
    }
    // §9: 잠긴 문서는 입력칸이 하나도 없는 읽기 전용 표로 그린다.
    function roLineEl(l) {
        const t = l.tax_type || (Number(l.tax_rate) === 0 ? 'zero' : 'taxable');
        const r = document.createElement('div'); r.className = 'erp-line ro';
        const cell = (cls, text, col) => { const s = document.createElement('span'); s.className = cls; if (col) s.setAttribute('data-col-' + col, ''); if (text != null) s.textContent = String(text); r.appendChild(s); return s; };
        cell('erp-c-handle no-print', '');
        const name = cell('erp-c-name', null);
        const b = document.createElement('b'); b.className = 'erp-ro-name'; b.textContent = l.name || ''; name.appendChild(b);
        if (l.note) { const n = document.createElement('small'); n.className = 'erp-ro-note'; n.textContent = l.note; name.appendChild(n); }
        cell('erp-c-spec', l.spec || '');
        cell('erp-c-qty rec-num', l.qty == null ? '' : l.qty);
        cell('erp-c-unit', l.unit || '');
        cell('erp-c-price rec-num', money(l.unit_price || 0));
        cell('erp-c-disc rec-num', l.discount_rate ? l.discount_rate : '', 'discount');
        cell('erp-c-tax', typeLabel(t));
        cell('erp-c-amount erp-amount rec-num', '');
        cell('erp-c-taxamt erp-taxamt rec-num', '');
        cell('no-print erp-c-x erp-x-cell', '');
        r.dataset.itemId = l.item_id || '';
        r.dataset.taxType = t;
        r._line = { item_id: l.item_id || '', name: l.name || '', spec: l.spec || '', unit: l.unit || '', note: l.note || '',
            qty: Math.round((Number(l.qty) || 0) * 1000) / 1000, unit_price: Math.round(Number(l.unit_price) || 0),
            discount_rate: Math.min(100, Math.max(0, Math.round(Number(l.discount_rate) || 0))), tax_type: t, tax_rate: TAX_RATE[t] };
        return r;
    }
    function lineEl(l) {
        if (locked) return roLineEl(l);
        const r = document.createElement('div'); r.className = 'erp-line';
        r.innerHTML = '<span class="erp-c-handle no-print"><span class="erp-handle" draggable="true" title="drag">⋮⋮</span></span>'
            + '<span class="erp-c-name"><span class="erp-name-wrap"><input class="form-input erp-name" maxlength="200" autocomplete="off" placeholder=""><span class="erp-item-code mono" hidden></span></span><input class="form-input erp-note sub" maxlength="200" hidden></span>'
            + '<span class="erp-c-spec"><input class="form-input erp-spec sub" maxlength="100" autocomplete="off"></span>'
            + '<span class="erp-c-qty"><input class="form-input erp-qty num" inputmode="decimal" min="0"></span>'
            + '<span class="erp-c-unit"><input class="form-input erp-unit sub" maxlength="20" autocomplete="off"></span>'
            + '<span class="erp-c-price"><input class="form-input erp-price num" inputmode="numeric"></span>'
            + '<span class="erp-c-disc" data-col-discount><input class="form-input erp-disc num" inputmode="numeric" maxlength="3"></span>'
            + '<span class="erp-c-tax"><button type="button" class="prop-pill neutral erp-tax-pill" tabindex="0"></button></span>'
            + '<span class="erp-c-amount erp-amount rec-num"></span>'
            + '<span class="erp-c-taxamt erp-taxamt rec-num"></span>'
            + '<span class="no-print erp-c-x erp-x-cell"><button type="button" class="crm-x erp-remove" tabindex="-1" aria-label="' + (L.remove || '삭제') + '">✕</button></span>';
        r.querySelector('.erp-name').value = l.name || '';
        r.querySelector('.erp-spec').value = l.spec || '';
        r.querySelector('.erp-unit').value = l.unit || '';
        r.querySelector('.erp-qty').value = l.qty ?? 1;
        r.querySelector('.erp-price').value = money(l.unit_price || 0);
        r.querySelector('.erp-disc').value = l.discount_rate ? l.discount_rate : '';
        r.querySelector('.erp-spec').placeholder = L.spec || '규격';
        r.querySelector('.erp-unit').placeholder = L.unit || '단위';
        r.querySelector('.erp-note').placeholder = L.lineNote || '비고';
        r.querySelector('.erp-disc').placeholder = '0';
        r.dataset.itemId = l.item_id || '';
        setTaxPill(r, l.tax_type || (Number(l.tax_rate) === 0 ? 'zero' : 'taxable'));
        setNote(r, l.note, false);
        const it = l.item_id ? items.find((x) => x.id === l.item_id) : null;
        setCode(r, it ? it.code : '');
        return r;
    }
    function lineData(r) {
        if (r._line) return Object.assign({}, r._line); // 읽기 전용 행
        const t = r.dataset.taxType || defaultType;
        return {
            item_id: r.dataset.itemId || '',
            name: r.querySelector('.erp-name').value.trim(),
            spec: r.querySelector('.erp-spec').value.trim(),
            unit: r.querySelector('.erp-unit').value.trim(),
            note: r.querySelector('.erp-note').value.trim(),
            qty: Math.round(num(r.querySelector('.erp-qty').value) * 1000) / 1000,
            unit_price: Math.round(num(r.querySelector('.erp-price').value)),
            discount_rate: Math.min(100, Math.max(0, Math.round(num(r.querySelector('.erp-disc').value)))),
            tax_type: t,
            tax_rate: TAX_RATE[t],
        };
    }
    function all() { return Array.from(rows.querySelectorAll('.erp-line')); }
    function collect() { return all().map(lineData).filter((l) => l.name); }
    function serialize() { return JSON.stringify(collect()); }
    function hasDiscount() { return all().some((r) => (lineData(r).discount_rate || 0) > 0); }
    function showDiscountCol(on) { showDisc = !!on; container.classList.toggle('show-discount', showDisc); }
    function render(lines) {
        rows.innerHTML = '';
        ((lines && lines.length) ? lines : [blank()]).forEach((l) => rows.appendChild(lineEl(l)));
        showDiscountCol(showDisc || hasDiscount());
        recalc();
    }
    // ── 계산 (models.LineTotals 미러) ──
    function lineAmount(qty, price, disc) { return Math.round(qty * price * (100 - disc) / 100); }
    function recalc() {
        const mode = taxMode(), rnd = rounding();
        const rs = all();
        const data = rs.map(lineData);
        let supply = 0, taxableSum = 0, lastTaxable = -1;
        const rdiv = (n, d) => (rnd === 'round' ? Math.round(n / d) : Math.floor(n / d));
        const supplies = data.map((l, i) => {
            const amount = lineAmount(l.qty, l.unit_price, l.discount_rate);
            const rate = (mode === 'none' || l.tax_type !== 'taxable') ? 0 : l.tax_rate;
            let s = amount, tIncl = 0;
            if (mode === 'inclusive' && rate > 0) { tIncl = rdiv(amount * rate, 100 + rate); s = amount - tIncl; } // 포함: 세액 먼저, 입력 금액 보존 (models.LineTotals)
            supply += s;
            if (rate > 0) { taxableSum += s; lastTaxable = i; }
            return { s, rate, tIncl };
        });
        let discount = Math.min(docDiscount(), supply);
        const allocBase = Math.min(discount, taxableSum);
        let allocated = 0, tax = 0;
        supplies.forEach((x, i) => {
            let t = 0;
            if (x.rate > 0) {
                let after = x.s, a = 0;
                if (allocBase > 0 && taxableSum > 0) {
                    a = Math.floor(allocBase * x.s / taxableSum);
                    if (i === lastTaxable) a = allocBase - allocated;
                    if (a > x.s) a = x.s;
                    allocated += a; after -= a;
                }
                t = (mode === 'inclusive' && a === 0) ? x.tIncl : rdiv(after * x.rate, 100);
            }
            tax += t;
            rs[i].querySelector('.erp-amount').textContent = x.s ? money(x.s) : '';
            rs[i].querySelector('.erp-taxamt').textContent = t ? money(t) : (x.s && mode !== 'none' && data[i].tax_type === 'taxable' ? '0' : '');
            rs[i].classList.toggle('no-tax', mode === 'none' || data[i].tax_type !== 'taxable');
        });
        const total = supply - discount + tax;
        const set = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = money(v); };
        set('erpSubtotal', supply); set('erpTax', tax); set('erpTotal', total);
        const out = { sub: supply, discount, tax, total };
        if (o.onTotals) o.onTotals(out);
        return out;
    }
    function focusName(r) { if (r) r.querySelector('.erp-name').focus(); }
    function addLine(data, at, where) {
        if (locked) return null;
        const r = lineEl(data || blank());
        if (at && where === 'before') rows.insertBefore(r, at);
        else if (at && at.nextElementSibling) rows.insertBefore(r, at.nextElementSibling);
        else rows.appendChild(r);
        focusName(r); recalc(); notify();
        return r;
    }
    function removeLine(r, o2) {
        if (locked) return null;
        const index = all().indexOf(r);
        const data = lineData(r);
        const prev = r.previousElementSibling;
        r.remove();
        if (!rows.children.length) rows.appendChild(lineEl(blank()));
        recalc(); notify();
        if (!(o2 && o2.silent)) {
            showToast(L.lineRemovedUndo || '줄을 삭제했습니다', {
                actionLabel: L.undo || '실행 취소',
                onAction: () => { const ref = all()[index]; addLine(data, ref, ref ? 'before' : undefined); },
            });
        }
        return prev;
    }
    function duplicate(r) { return addLine(lineData(r), r, 'after'); }
    function hint(input, text) {
        const cell = input.parentElement;
        let h = cell.querySelector('.erp-qty-hint');
        if (!h) { h = document.createElement('span'); h.className = 'erp-qty-hint'; cell.appendChild(h); }
        h.textContent = text; h.classList.add('show');
        clearTimeout(h._t); h._t = setTimeout(() => h.classList.remove('show'), 1800);
    }
    // 품목 적용 (피커 선택 / 정확히 일치하는 이름·코드 입력)
    function applyItem(r, it) {
        r.dataset.itemId = it.id;
        r.querySelector('.erp-name').value = it.name;
        if (it.spec) r.querySelector('.erp-spec').value = it.spec;
        if (it.unit) r.querySelector('.erp-unit').value = it.unit;
        const acct = acctPrices[it.id];
        r.querySelector('.erp-price').value = money((acct === undefined || acct === null ? it.unit_price : acct) || 0);
        setTaxPill(r, it.tax_type || (Number(it.tax_rate) === 0 ? 'zero' : 'taxable'));
        setCode(r, it.code);
        recalc(); notify();
    }

    // ── 품목 피커 (.prop-pop) ──
    let pop = null, popRow = null, popIdx = 0, popList = [];
    function closePicker() { if (pop) { pop.remove(); pop = null; popRow = null; popList = []; } }
    function renderPicker() {
        if (!pop) return;
        const list = pop.querySelector('.prop-pop-list');
        list.innerHTML = '';
        popList.forEach((entry, i) => {
            const b = document.createElement('button'); b.type = 'button';
            b.className = 'prop-pop-item erp-item-row' + (i === popIdx ? ' sel' : '') + (entry.isNew ? ' new' : '');
            if (entry.isNew) {
                b.textContent = (L.newItemInline || '새 품목') + ': ' + entry.name;
            } else {
                const it = entry.item;
                const code = document.createElement('span'); code.className = 'mono erp-ip-code'; code.textContent = it.code || '—';
                const name = document.createElement('span'); name.className = 'erp-ip-name'; name.textContent = it.name;
                const price = document.createElement('span'); price.className = 'erp-ip-price mono'; price.textContent = money(it.unit_price || 0);
                const unit = document.createElement('span'); unit.className = 'erp-ip-unit'; unit.textContent = it.unit || '';
                b.appendChild(code); b.appendChild(name); b.appendChild(price); b.appendChild(unit);
                const ap = acctPrices[it.id];
                if (ap !== undefined && ap !== null) { price.textContent = money(ap); const badge = document.createElement('span'); badge.className = 'erp-ip-acct'; badge.textContent = L.accountPrice || '거래처 단가'; b.appendChild(badge); }
            }
            b.addEventListener('mousedown', (e) => e.preventDefault());
            b.addEventListener('click', () => choosePicker(i));
            list.appendChild(b);
        });
        if (!popList.length) { const e = document.createElement('div'); e.className = 'prop-pop-empty'; e.textContent = '—'; list.appendChild(e); }
        const sel = list.querySelector('.sel'); if (sel && sel.scrollIntoView) sel.scrollIntoView({ block: 'nearest' });
    }
    function openPicker(r, q) {
        if (locked) return;
        const input = r.querySelector('.erp-name');
        const query = (q === undefined ? input.value : q).trim();
        const matched = items.filter((it) => it.active !== false && itemMatches(it, query)).slice(0, 12);
        popList = matched.map((it) => ({ item: it }));
        if (o.canManage && query && !items.some((x) => x.name.toLowerCase() === query.toLowerCase())) popList.push({ isNew: true, name: query });
        if (!popList.length) { closePicker(); return; }
        if (!pop || popRow !== r) {
            closePicker();
            pop = document.createElement('div'); pop.className = 'prop-pop erp-item-pop'; pop.setAttribute('role', 'listbox');
            const head = document.createElement('div'); head.className = 'erp-item-pop-hint'; head.textContent = L.pickItemHint || '↑↓ 선택 · Enter 채우기 · Esc 직접 입력';
            const list = document.createElement('div'); list.className = 'prop-pop-list';
            pop.appendChild(list); pop.appendChild(head);
            document.body.appendChild(pop);
            popRow = r;
            const rect = input.getBoundingClientRect();
            pop.style.left = Math.max(4, Math.min(rect.left, window.innerWidth - 420)) + 'px';
            pop.style.top = Math.min(rect.bottom + 4, window.innerHeight - 260) + 'px';
            pop.style.minWidth = Math.max(280, rect.width) + 'px';
        }
        popIdx = 0;
        renderPicker();
    }
    function choosePicker(i) {
        const entry = popList[i]; const r = popRow;
        if (!entry || !r) return;
        if (entry.isNew) {
            if (!o.post) return;
            o.post('/api/erp/items', { name: entry.name }, L).then((it) => { items.push(it); applyItem(r, it); }).catch((e) => showToast((e && e.message) || 'failed', { error: true }));
        } else {
            applyItem(r, entry.item);
        }
        closePicker();
        const next = r.querySelector('.erp-spec'); if (next && !next.readOnly) next.focus();
    }
    document.addEventListener('mousedown', (e) => { if (pop && !pop.contains(e.target) && !(popRow && popRow.contains(e.target))) closePicker(); }, true);
    window.addEventListener('scroll', closePicker, true);

    // ── 이벤트 ──
    rows.addEventListener('input', (e) => {
        const r = e.target.closest('.erp-line'); if (!r || locked) return;
        if (e.target.classList.contains('erp-name')) {
            const v = e.target.value.trim();
            const it = items.find((x) => x.name === v || (x.code && x.code === v));
            if (it) { applyItem(r, it); closePicker(); return; }
            r.dataset.itemId = ''; setCode(r, '');
            if (v) openPicker(r, v); else closePicker();
        }
        if (e.target.classList.contains('erp-qty') && num(e.target.value) < 0) { e.target.value = 0; hint(e.target, L.qtyMinHint || '수량은 0 이상이어야 합니다'); }
        if (e.target.classList.contains('erp-disc')) {
            const d = num(e.target.value);
            if (d < 0) e.target.value = 0; else if (d > 100) e.target.value = 100;
            if (num(e.target.value) > 0) showDiscountCol(true);
        }
        recalc(); notify();
    });
    rows.addEventListener('blur', (e) => {
        if (e.target.classList.contains('erp-price')) e.target.value = money(num(e.target.value));
        if (e.target.classList.contains('erp-qty') && num(e.target.value) < 0) { e.target.value = 0; recalc(); notify(); }
        if (e.target.classList.contains('erp-disc') && !num(e.target.value)) e.target.value = '';
    }, true);
    rows.addEventListener('keydown', (e) => {
        const r = e.target.closest('.erp-line'); if (!r || locked) return;
        const isName = e.target.classList.contains('erp-name');
        if (isName && pop && popRow === r) {
            if (e.key === 'ArrowDown') { e.preventDefault(); popIdx = Math.min(popList.length - 1, popIdx + 1); renderPicker(); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); popIdx = Math.max(0, popIdx - 1); renderPicker(); return; }
            if (e.key === 'Enter') { e.preventDefault(); choosePicker(popIdx); return; }
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closePicker(); return; }
        }
        if (isName && e.key === '/' && !e.target.value) { e.preventDefault(); openPicker(r, ''); return; }
        const lastCell = e.target.classList.contains('erp-tax-pill') || (e.target.classList.contains('erp-disc') && !container.classList.contains('show-discount'));
        if (e.key === 'Tab' && !e.shiftKey && e.target.classList.contains('erp-tax-pill') && !r.nextElementSibling) { e.preventDefault(); addLine(); return; }
        if (e.key === 'Enter' && !e.target.classList.contains('erp-tax-pill')) {
            e.preventDefault();
            if (e.shiftKey) { addLine(null, r, 'after'); return; }
            if (!r.nextElementSibling) addLine(); else focusName(r.nextElementSibling);
            return;
        }
        if (e.key === 'Backspace' && isName && !e.target.value && all().length > 1) { e.preventDefault(); closePicker(); focusName(removeLine(r) || rows.firstElementChild); return; }
        if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            e.preventDefault();
            const cls = e.target.className;
            if (e.key === 'ArrowUp' && r.previousElementSibling) rows.insertBefore(r, r.previousElementSibling);
            if (e.key === 'ArrowDown' && r.nextElementSibling) rows.insertBefore(r.nextElementSibling, r);
            const again = r.querySelector('.' + cls.split(' ').find((c) => c.startsWith('erp-'))); if (again) again.focus();
            notify();
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicate(r); }
        void lastCell;
    });
    rows.addEventListener('click', (e) => {
        if (locked) return;
        const x = e.target.closest('.erp-remove'); if (x) { removeLine(x.closest('.erp-line')); return; }
        const pill = e.target.closest('.erp-tax-pill');
        if (pill) {
            const r = pill.closest('.erp-line');
            const options = TAX_TYPES.map((t) => ({ label: typeLabel(t), color: 'o8' }));
            openPillPicker(pill, { options, values: [typeLabel(r.dataset.taxType)], neutral: true, required: true, placeholder: L.taxRate || '세율', onChange: (vals) => {
                const t = TAX_TYPES.find((tt) => typeLabel(tt) === vals[0]) || defaultType;
                setTaxPill(r, t); recalc(); notify();
            } });
        }
    });
    rows.addEventListener('contextmenu', (e) => {
        const r = e.target.closest('.erp-line'); if (!r || locked) return;
        e.preventDefault();
        const noteShown = !r.querySelector('.erp-note').hidden;
        showContextMenu(e.clientX, e.clientY, [
            { label: L.menuAddAbove || '위에 줄 추가', onClick: () => addLine(null, r, 'before') },
            { label: L.menuAddBelow || '아래에 줄 추가', onClick: () => addLine(null, r, 'after'), hint: 'Shift+Enter' },
            { label: L.menuDuplicate || '복제', onClick: () => duplicate(r), hint: 'Ctrl+D' },
            { separator: true },
            { label: noteShown ? (L.removeNote || '비고 제거') : (L.addNote || '비고 추가'), onClick: () => { if (noteShown) { setNote(r, '', false); recalc(); notify(); } else { setNote(r, r.querySelector('.erp-note').value, true); r.querySelector('.erp-note').focus(); } } },
            { label: container.classList.contains('show-discount') ? (L.hideDiscountCol || '할인% 열 숨기기') : (L.showDiscountCol || '할인% 열 표시'), onClick: () => showDiscountCol(!container.classList.contains('show-discount')) },
            { separator: true },
            { label: L.delete || '삭제', danger: true, onClick: () => removeLine(r) },
        ]);
    });
    // 헤더 우클릭: 할인% 열 토글
    const head = container.querySelector('.erp-line.head');
    if (head) head.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        showContextMenu(e.clientX, e.clientY, [{ label: container.classList.contains('show-discount') ? (L.hideDiscountCol || '할인% 열 숨기기') : (L.showDiscountCol || '할인% 열 표시'), onClick: () => showDiscountCol(!container.classList.contains('show-discount')) }]);
    });
    // ⋮⋮ 드래그로 순서 변경
    let dragging = null;
    rows.addEventListener('dragstart', (e) => {
        const h = e.target.closest && e.target.closest('.erp-handle'); if (!h || locked) { e.preventDefault(); return; }
        dragging = h.closest('.erp-line'); dragging.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', 'line'); } catch (_) { /* */ }
    });
    rows.addEventListener('dragover', (e) => {
        if (!dragging) return;
        e.preventDefault();
        const over = e.target.closest('.erp-line'); if (!over || over === dragging) return;
        const rect = over.getBoundingClientRect();
        if (e.clientY < rect.top + rect.height / 2) rows.insertBefore(dragging, over); else rows.insertBefore(dragging, over.nextElementSibling);
    });
    rows.addEventListener('dragend', () => { if (dragging) { dragging.classList.remove('dragging'); dragging = null; notify(); } });
    rows.addEventListener('drop', (e) => e.preventDefault());

    function setLocked(v) {
        const cur = collect();
        locked = !!v;
        container.classList.toggle('locked', locked);
        closePicker(); closePillPicker();
        render(cur); // 편집기 ↔ 읽기 전용 표 전환 (§9)
        const add = document.getElementById('erpAddLine'); if (add) add.hidden = locked;
    }
    function setTaxMode(mode) { void mode; recalc(); }

    container.classList.toggle('locked', locked);
    render(o.lines || []);
    return { collect, serialize, render, addLine: () => addLine(), recalc, setLocked, setTaxMode, showDiscountCol, hasDiscount, closePicker, setAccountPrices: (m) => { acctPrices = m || {}; } };
}
